import React, { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useNotifications } from '../../../../../notifications';
import { useNavigate, fillPlaceholders, paths } from '../../../../../routing';
import { queriesV2 } from '../../../../../api';
import {
  createImportedDocument,
  createDocumentTemplate,
  moveTemplateToFolder,
  updateTemplateEditorConfiguration,
  extractDocumentData,
} from '../../../../../api/mutations';
import { TemplateType } from '../../../../../consts/forms-types';
import FileUploadModal from '../common/FileUploadModal';
import { BaseUploadProps, UploadFileData } from '../common/types';
import { prepareFileForUpload, prepareFilesForUpload } from '../common/utils';

interface PdfUploadContainerProps extends BaseUploadProps {
  templateUpload?: boolean;
  isPremiumUser?: boolean;
}

const PdfUploadContainer: React.FC<PdfUploadContainerProps> = ({
  isOpen,
  onClose,
  teamId,
  dealId,
  templateUpload = false,
  folders,
  selectedFolderId,
  onFolderSelectionChange,
  foldersLoading,
  isClauseUpload,
  isPremiumUser = false,
}) => {
  const [, { error: notifyError }] = useNotifications();
  const navigate = useNavigate();

  // Deal PDF upload mutation
  const [createImportedDocumentMutation, { loading: createImportedDocumentLoading }] = useMutation(
    createImportedDocument,
    {
      refetchQueries: dealId
        ? [
            {
              query: queriesV2.getDocumentsByDeal,
              variables: { dealId },
            },
          ]
        : [],
    }
  );

  // Template PDF upload mutation
  const [createDocumentTemplateMutation, { loading: createDocumentTemplateLoading }] = useMutation(
    createDocumentTemplate,
    {
      refetchQueries: [
        {
          query: queriesV2.getDocumentTemplates,
          variables: { teamId },
        },
      ],
    }
  );

  const [moveTemplateToFolderMutation] = useMutation(moveTemplateToFolder);
  const [updateTemplateEditorConfigurationMutation] = useMutation(updateTemplateEditorConfiguration);
  const [extractDocumentDataMutation] = useMutation(extractDocumentData);

  const handleUpload = useCallback(
    async ({
      documentTitle,
      selectedFile,
      selectedFiles,
      isDisableWordExportEnabled,
      selectedFolderId: folderId,
      extractDocumentTerms,
    }: UploadFileData) => {
      try {
        const hasMultipleFiles = selectedFiles && selectedFiles.length > 0;
        const hasSingleFile = selectedFile && selectedFile.fileData;

        if (!hasMultipleFiles && !hasSingleFile) return;

        // Prepare file data for mutation
        const filesArray = hasMultipleFiles ? selectedFiles : selectedFile ? [selectedFile] : [];
        const fileData = filesArray.length === 1 ? prepareFileForUpload(filesArray[0]) : undefined;
        const filesData = filesArray.length > 1 ? prepareFilesForUpload(filesArray) : undefined;

        if (templateUpload) {
          // Template upload
          const result = await createDocumentTemplateMutation({
            variables: {
              teamId,
              name: documentTitle,
              templateType: TemplateType.DOCUMENT,
              fileData,
              filesData,
              restrictedEditing: false, // PDFs don't support restricted editing
            },
          });

          const templateId = result.data?.createDocumentTemplate?.id;

          if (templateId) {
            // Update editor configuration if needed
            if (isDisableWordExportEnabled) {
              await updateTemplateEditorConfigurationMutation({
                variables: {
                  templateId,
                  configuration: {
                    disableWordExport: isDisableWordExportEnabled,
                  },
                },
              });
            }

            // Move to folder if selected
            if (folderId) {
              await moveTemplateToFolderMutation({
                variables: {
                  templateId,
                  folderId,
                },
              });
            }

            // Navigate to template
            const templateUrl = fillPlaceholders(paths.templates.document, {
              templateId,
              teamId,
            });
            navigate(templateUrl);
          }
        } else {
          // Deal document upload
          const result = await createImportedDocumentMutation({
            variables: {
              dealId: dealId!,
              teamId,
              title: documentTitle,
              fileData,
              filesData,
            },
          });

          if (result.data?.createImportedDocument) {
            const documentId = result.data.createImportedDocument.id;

            // Trigger extraction if requested
            if (extractDocumentTerms) {
              try {
                await extractDocumentDataMutation({
                  variables: { documentId },
                });
              } catch (extractError) {
                // Log error but don't block navigation
                console.error('Extraction failed:', extractError);
                notifyError({
                  message: {
                    id: 'extraction-error',
                    defaultMessage: 'Document uploaded, but extraction failed. You can retry from the document actions menu.',
                  },
                });
              }
            }

            // Navigate to document
            const navigateUrl = fillPlaceholders(paths.documentPath, {
              dealId: dealId!,
              teamId,
              documentId,
            });

            // Note: We don't dispatch to store here as the refetchQueries will update the data
            navigate(navigateUrl);
          }
        }

        onClose();
      } catch (error) {
        notifyError({
          message: {
            id: 'pdf-upload-error',
            defaultMessage: `PDF upload error: ${error}`,
          },
        });
      }
    },
    [
      createImportedDocumentMutation,
      createDocumentTemplateMutation,
      moveTemplateToFolderMutation,
      updateTemplateEditorConfigurationMutation,
      extractDocumentDataMutation,
      templateUpload,
      teamId,
      dealId,
      navigate,
      notifyError,
      onClose,
    ]
  );

  return (
    <FileUploadModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleUpload}
      isLoading={createImportedDocumentLoading || createDocumentTemplateLoading}
      templateUpload={templateUpload}
      allowMultiple={true}
      folders={folders}
      selectedFolderId={selectedFolderId}
      onFolderSelectionChange={onFolderSelectionChange}
      foldersLoading={foldersLoading}
      isClauseUpload={isClauseUpload}
      modalTitle="Upload PDF"
      acceptedFileTypes=".pdf"
      showRestrictedEditing={false}
      showDisableWordExport={false}
      showExtractDocumentTerms={!templateUpload && isPremiumUser}
    />
  );
};

export default PdfUploadContainer;